import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCode: string | null;
}

/**
 * Top-level error boundary to prevent white-screen crashes.
 * Catches render errors and displays a branded fallback UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null, errorCode: null };

  public static getDerivedStateFromError(error: Error): State {
    // Short timestamp-based code for support — no internal IDs exposed
    const errorCode = `E-${Date.now().toString(36).slice(-6).toUpperCase()}`;
    return { hasError: true, error, errorCode };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary] Uncaught error (${this.state.errorCode}):`, error, errorInfo);
    Sentry.captureException(error, {
      tags: { errorCode: this.state.errorCode ?? undefined },
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorCode: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
              <p className="text-muted-foreground">
                An unexpected error occurred. Please try again or contact support if the problem persists.
              </p>
              {this.state.errorCode && (
                <p className="text-xs text-muted-foreground font-mono">
                  Error code: {this.state.errorCode}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = "/"; }}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border text-foreground font-medium hover:bg-muted transition-colors"
              >
                Go Home
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="text-left bg-muted/50 rounded-lg p-4 text-xs">
                <summary className="cursor-pointer text-muted-foreground mb-2">
                  Error Details (dev only)
                </summary>
                <pre className="whitespace-pre-wrap text-destructive overflow-auto max-h-40">
                  {this.state.error.message}
                  {"\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
